import DataTable from "../../components/common/DataTable";
import { Search } from "lucide-react";
import { Avatar, Button, TextInput, Select, Text, Group } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import React from "react";
import { useNavigate } from "react-router-dom";
import Head from "../../components/common/Head";
import useValidate from "../../hooks/useValidate";
import { deleteCreator, getAllCreators, updateCreator } from "../../utils/api";
import useStore from "../../utils/store";

const NonVerifiedCreators = () => {
  const navigate = useNavigate();
  const user = useStore((state) => state.user);

  const [creators, setCreators] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");
  const [pagination, setPagination] = React.useState({ current: 1, pageSize: 10, total: 0 });

  const hasPermission = React.useMemo(() => user?.getNonVerifiedCreators || false, [user]);
  const getPermissions = React.useMemo(() => ({
    addCreator: user?.addCreators || false,
    editCreator: user?.editCreators || false,
    deleteCreator: user?.deleteCreators || false,
    verifyCreator: user?.editCreators || false,
  }), [user]);

  const fetchCreators = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    const [success, response] = await getAllCreators(page, pageSize, { isVerified: false });
    if (success) {
      setCreators(response.data || []);
      setPagination((p) => ({ ...p, current: response.metadata?.currentPage || 1, total: response.metadata?.totalData || 0, pageSize }));
    }
    setLoading(false);
  };

  const handleStatusChange = async (value, record) => {
    const [success] = await updateCreator(record._id, { isDisable: value === "disabled" });
    if (success) notifications.show({ message: "Status updated", color: "green" });
    else notifications.show({ message: "Failed to update status", color: "red" });
  };

  const handleVerifyCreator = async (id) => {
    const [success] = await updateCreator(id, { isVerified: true });
    if (success) { notifications.show({ message: "Creator verified successfully", color: "green" }); fetchCreators(); }
    else notifications.show({ message: "Failed to verify creator", color: "red" });
  };

  const filteredCreators = React.useMemo(() => {
    if (!searchText) return creators;
    return creators.filter((c) =>
      c.name.toLowerCase().includes(searchText.toLowerCase()) ||
      c.email.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [creators, searchText]);

  const showDeleteConfirm = (id, name) => {
    modals.openConfirmModal({
      title: `Delete creator "${name}"?`,
      children: <Text size="sm">This action cannot be undone.</Text>,
      labels: { confirm: "Yes, Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        const [success] = await deleteCreator(id);
        if (success) { notifications.show({ message: "Creator deleted", color: "green" }); fetchCreators(); }
        else notifications.show({ message: "Failed to delete creator", color: "red" });
      },
    });
  };

  useValidate();

  React.useEffect(() => {
    if (hasPermission) fetchCreators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <Head
        title="Non-Verified Partners"
        customButton={
          getPermissions.addCreator ? <Button onClick={() => navigate("/partners/add")}>Add Partner</Button> : null
        }
      />
      {!hasPermission ? (
        <Text c="dimmed" ta="center" py="xl">You don't have permission to view non-verified partners</Text>
      ) : (
        <>
          <TextInput
            placeholder="Search by name or email"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            leftSection={<Search size={16} />}
            clearable
            mb="md"
            style={{ maxWidth: 320 }}
          />
          <DataTable
            loading={loading}
            dataSource={searchText ? filteredCreators : creators}
            rowKey="_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: searchText ? filteredCreators.length : pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={(pag) => fetchCreators(pag.current, pag.pageSize)}
            columns={[
              { title: "Profile", dataIndex: "image", key: "profilePic", render: (src) => <Avatar src={src} /> },
              { title: "Name", dataIndex: "name", key: "name" },
              { title: "Email", dataIndex: "email", key: "email" },
              { title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber" },
              {
                title: "Status",
                dataIndex: "isDisable",
                key: "isDisable",
                render: (isDisable, record) => (
                  <Select
                    value={isDisable ? "disabled" : "enabled"}
                    onChange={(value) => handleStatusChange(value, record)}
                    data={[{ value: "enabled", label: "Enabled" }, { value: "disabled", label: "Disabled" }]}
                    w={120}
                  />
                ),
              },
              {
                title: "Actions",
                key: "actions",
                render: (_, record) => (
                  <Group gap="xs">
                    {getPermissions.verifyCreator && (
                      <Button size="xs" color="green" onClick={() => handleVerifyCreator(record._id)}>Verify</Button>
                    )}
                    {getPermissions.editCreator && (
                      <Button size="xs" onClick={() => navigate(`/partners/edit/${record._id}`)}>Edit</Button>
                    )}
                    {getPermissions.deleteCreator && (
                      <Button size="xs" color="red" variant="light" onClick={() => showDeleteConfirm(record._id, record.name)}>Delete</Button>
                    )}
                  </Group>
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default NonVerifiedCreators;
